import {
  BookOpen,
  Lightbulb,
  MessageCircleQuestionMark,
  SquareArrowOutUpRight,
  Zap,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { generatePath, useParams } from 'react-router-dom';
import { IndexPath } from '@/Config/app/router/index.path';
import { WhyAISaidThatDto } from '@/Domain/api/Conversation/dto/WhyAISaidThat.dto';
import { Badge } from '@/Domain/components/Shared/Badge/Badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';

type Props = {
  whyAISaidThat: WhyAISaidThatDto;
};

const getImportanceBadgeVariant = (
  importance: number | null,
): 'success' | 'warning' | 'secondary' => {
  if (importance === null) return 'secondary';
  if (importance >= 8) return 'success';
  if (importance >= 5) return 'warning';
  return 'secondary';
};

export const WhyAISaidThatDisplay = ({ whyAISaidThat }: Props) => {
  const { t } = useTranslation('inbox_v2');

  const getDisplayFieldName = (field: string): string => {
    const translationKey = `why_ai_said_that_popover.field_names.${field}`;
    const translated = t(translationKey);
    // If the translation key doesn't exist, t() returns the key itself
    return translated === translationKey ? field : translated;
  };

  const getDisplayActionName = (name: string): string => {
    const translationKey = `why_ai_said_that_popover.action_names.${name}`;
    const translated = t(translationKey);
    return translated === translationKey ? name : translated;
  };
  const { scenario } = useParams();

  const { explanation, metadata, ai_actions_used, improvement_hint } = whyAISaidThat;

  // Sort sources by importance (highest first)
  const sortedSources = explanation?.sources
    ? [...explanation.sources].sort((a, b) => {
        const importanceA = a.importance ?? 0;
        const importanceB = b.importance ?? 0;
        return importanceB - importanceA;
      })
    : [];

  const getArticleUrl = (articleId: number) =>
    `/i/${scenario}/${generatePath(IndexPath.knowledgeBase.article, { id: articleId })}`;

  return (
    <TooltipProvider>
      <div
        className="flex flex-col gap-5 hide-scrollbar h-full"
        data-testid="why-ai-said-that-display"
      >
        {/* Header Section */}
        {explanation?.summary && (
          <section className="flex flex-col gap-3" data-testid="why-ai-said-that-summary-section">
            <div className="flex items-center gap-2.5">
              <div className="p-1.5 rounded-md bg-accent">
                <MessageCircleQuestionMark className="text-muted-foreground size-4" />
              </div>
              <h3 className="text-sm font-bold text-foreground tracking-tight">
                {t('why_ai_said_that_popover.title')}
              </h3>
            </div>
            <p
              className="text-xs text-muted-foreground leading-relaxed"
              data-testid="why-ai-said-that-summary"
            >
              {explanation.summary}
            </p>
          </section>
        )}

        {/* AI Actions Used Section */}
        {ai_actions_used && ai_actions_used.length > 0 && (
          <section className="flex flex-col gap-2.5" data-testid="why-ai-said-that-actions-section">
            <h4 className="text-xs font-semibold text-foreground-subtle uppercase tracking-wide">
              {t('why_ai_said_that_popover.ai_actions_used')}
            </h4>
            <div className="flex flex-col gap-2">
              {ai_actions_used.map((action, index) => (
                <div
                  key={index}
                  className="flex items-start gap-2.5 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 rounded-xl p-3 overflow-hidden"
                  data-testid={`why-ai-said-that-action-${index}`}
                >
                  <div className="p-1 rounded-md bg-blue-100 shrink-0">
                    <Zap className="text-blue-600 size-3.5" />
                  </div>
                  <div className="flex flex-col gap-0.5 min-w-0 overflow-hidden">
                    <p
                      className="text-xs font-semibold text-blue-900 truncate"
                      data-testid={`why-ai-said-that-action-name-${index}`}
                    >
                      {getDisplayActionName(action.name)}
                    </p>
                    {action.args_summary && (
                      <p
                        className="text-xs text-blue-600/80 break-words leading-relaxed line-clamp-3"
                        data-testid={`why-ai-said-that-action-args-${index}`}
                      >
                        {action.args_summary}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Sources Section */}
        {sortedSources.length > 0 && (
          <section className="flex flex-col gap-3" data-testid="why-ai-said-that-sources-section">
            <div className="flex items-center justify-between">
              <h4 className="text-xs font-semibold text-foreground-subtle uppercase tracking-wide">
                {t('why_ai_said_that_popover.sources')}
              </h4>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="text-xs text-foreground-subtle font-medium cursor-help">
                    {t('why_ai_said_that_popover.score_equals_influence')}
                  </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  {t('why_ai_said_that_popover.score_explanation')}
                </TooltipContent>
              </Tooltip>
            </div>
            <div className="flex flex-col gap-3">
              {sortedSources.map((source, index) => (
                <div
                  key={index}
                  className="flex flex-col gap-2.5 p-3 bg-card rounded-xl border border-border shadow-sm"
                  data-testid={`why-ai-said-that-source-${index}`}
                >
                  {/* Source Header with Importance and Pointer */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      {source.pointer && (
                        <p className="text-xs font-medium text-foreground-subtle uppercase tracking-wide mb-1">
                          {getDisplayFieldName(source.pointer)}
                        </p>
                      )}
                      {source.article_id && source.article_scenario_id === scenario ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <a
                              href={getArticleUrl(source.article_id)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 cursor-pointer group"
                            >
                              <div className="p-1 rounded-md bg-muted group-hover:bg-primary-100 transition-colors">
                                <BookOpen className="shrink-0 text-muted-foreground group-hover:text-primary-600 size-3.5 transition-colors" />
                              </div>
                              <p
                                className="text-xs text-muted-foreground font-medium group-hover:text-primary-600 text-left break-text line-clamp-2 transition-colors"
                                data-testid={`why-ai-said-that-source-article-title-${index}`}
                              >
                                {source.article_title ||
                                  t('why_ai_said_that_popover.knowledge_base_article')}
                              </p>
                              <SquareArrowOutUpRight className="shrink-0 text-foreground-subtle group-hover:text-primary-600 size-3 transition-colors" />
                            </a>
                          </TooltipTrigger>
                          <TooltipContent>
                            {t('tooltip.view_article_in_knowledge_base')}
                          </TooltipContent>
                        </Tooltip>
                      ) : source.article_id ? (
                        <div className="flex items-center gap-2">
                          <div className="p-1 rounded-md bg-muted">
                            <BookOpen className="shrink-0 text-muted-foreground size-3.5" />
                          </div>
                          <p
                            className="text-xs text-muted-foreground font-medium text-left break-text line-clamp-2"
                            data-testid={`why-ai-said-that-source-article-title-${index}`}
                          >
                            {source.article_title ||
                              t('why_ai_said_that_popover.knowledge_base_article')}
                          </p>
                        </div>
                      ) : null}
                    </div>
                    {source.importance !== null && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Badge
                            variant={getImportanceBadgeVariant(source.importance)}
                            size="sm"
                            className="shrink-0 text-xs cursor-help"
                            data-testid={`why-ai-said-that-source-importance-${index}`}
                          >
                            {Math.round(source.importance)}/10
                          </Badge>
                        </TooltipTrigger>
                        <TooltipContent>{t('tooltip.importance_score')}</TooltipContent>
                      </Tooltip>
                    )}
                  </div>

                  {/* Source Snippet */}
                  {source.source_snippet && (
                    <div className="bg-muted/80 rounded-lg px-3 py-2.5 border-l-2 border-border">
                      <p
                        className="text-xs text-muted-foreground leading-relaxed italic"
                        data-testid={`why-ai-said-that-source-snippet-${index}`}
                      >
                        "{source.source_snippet}"
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Metadata Section */}
        {metadata && (
          <section className="flex flex-col gap-3">
            {/* Observed Editable Fields Used */}
            {metadata.observed_editable_fields_used &&
              metadata.observed_editable_fields_used.length > 0 && (
                <div className="flex flex-col gap-2">
                  <h4 className="text-xs font-semibold text-foreground-subtle uppercase tracking-wide">
                    {t('why_ai_said_that_popover.fields_used')}
                  </h4>
                  <div className="flex flex-wrap gap-1.5">
                    {metadata.observed_editable_fields_used.map((field, index) => (
                      <Badge
                        key={index}
                        variant="primary"
                        size="sm"
                        className="text-xs"
                        data-testid={`why-ai-said-that-field-used-${index}`}
                      >
                        {getDisplayFieldName(field)}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

            {/* Unused Editable Fields */}
            {metadata.unused_editable_fields && metadata.unused_editable_fields.length > 0 && (
              <div className="flex flex-col gap-2">
                <h4 className="text-xs font-semibold text-foreground-subtle uppercase tracking-wide">
                  {t('why_ai_said_that_popover.unused_fields')}
                </h4>
                <div className="flex flex-wrap gap-1.5">
                  {metadata.unused_editable_fields.map((field, index) => (
                    <Badge
                      key={index}
                      variant="secondary"
                      size="sm"
                      className="text-xs"
                      data-testid={`why-ai-said-that-unused-field-${index}`}
                    >
                      {getDisplayFieldName(field)}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </section>
        )}

        {/* Improvement Hint Section - at the bottom */}
        {improvement_hint && (
          <section
            className="flex items-start gap-2.5 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200/60 rounded-xl p-3 mt-1"
            data-testid="why-ai-said-that-improvement-hint-section"
          >
            <div className="p-1 rounded-md bg-amber-100">
              <Lightbulb className="shrink-0 text-amber-600 size-3.5" />
            </div>
            <div className="flex flex-col gap-0.5">
              <h4 className="text-xs font-semibold text-amber-800">
                {t('why_ai_said_that_popover.tip')}
              </h4>
              <p
                className="text-xs text-amber-700/90 leading-relaxed"
                data-testid="why-ai-said-that-improvement-hint"
              >
                {improvement_hint}
              </p>
            </div>
          </section>
        )}
      </div>
    </TooltipProvider>
  );
};
